import { Component, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { fromEvent, merge, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { EventBusBase } from '@skylab-shared/core';
import { Keys } from '@skylab-shared/ui';
import { isFunction } from '@skylab-shared/utils';

import { RoutingService } from '@skylab-angular-shared/core';

import { $, $Element } from '../utils/element';
import { MenuItem } from './menu.model';

@Component({
    template: '',
})
export class MenuComponentBase implements OnInit, OnDestroy {
    // bindings
    @Input() public menuId!: string;
    @Input() public collection!: MenuItem[];
    @Input() public context: unknown;
    @Input() public triggerElement?: HTMLElement;
    @Input() public hideOnScroll = true;
    @Input() public close!: ($event?: any) => void;
    @Input() public onEsc!: () => void;

    // event names
    private keydownEventName: string = `keydown.${uuid()}`;
    private keyupEventName: string = `keyup.${uuid()}`;
    private blurEventName: string = `blur.${uuid()}`;

    private stateChangedEventHandler!: string;
    private resizeEventHandler!: Function;
    private destroy$ = new Subject<void>();

    // element
    protected element: $Element;

    private readonly pointerDownHandler = (event: Event): void => {
        const target = event.target as Node | null;
        if (!target) return;

        if (this.element.element && this.element.element.contains(target)) {
            return;
        }

        if (this.triggerElement && (this.triggerElement === target || this.triggerElement.contains(target))) {
            return;
        }

        this.close(event);
    };

    private readonly scrollHandler = (event: Event): void => {
        if (!this.hideOnScroll) return;
        const target = event.target as Node | null;
        if (target && this.element.element && this.element.element.contains(target)) {
            return;
        }
        this.close(event);
    };

    public constructor(
        protected elRef: ElementRef,
        protected eventBus: EventBusBase,
        protected routingService: RoutingService
    ) {
        this.element = $(elRef.nativeElement);
    }

    public ngOnInit(): void {
        this.bindEvents();
    }

    public ngOnDestroy(): void {
        this.unbindEvents();
        this.destroy$?.next();
    }

    //#region - events
    protected bindEvents(): void {
        this.element.on(this.keydownEventName, this.onKeydown.bind(this));
        this.element.on(this.keyupEventName, this.onKeyup.bind(this));
        this.element.on(this.blurEventName, this.onBlur.bind(this));

        document.addEventListener('pointerdown', this.pointerDownHandler, { capture: true, passive: true });
        document.addEventListener('touchstart', this.pointerDownHandler, { capture: true, passive: true });
        window.addEventListener('scroll', this.scrollHandler, { capture: true, passive: true });

        merge(this.routingService.states$, fromEvent(window, 'resize'))
            .pipe(takeUntil(this.destroy$))
            .subscribe(_ => {
            this.close();
        });
    }

    public unbindEvents(): void {
        this.element.off(this.keydownEventName);
        this.element.off(this.keyupEventName);
        this.element.off(this.blurEventName);

        document.removeEventListener('pointerdown', this.pointerDownHandler, { capture: true } as EventListenerOptions);
        document.removeEventListener('touchstart', this.pointerDownHandler, { capture: true } as EventListenerOptions);
        window.removeEventListener('scroll', this.scrollHandler, { capture: true } as EventListenerOptions);

        this.eventBus.deleteObserver(this.stateChangedEventHandler);
        if (isFunction(this.resizeEventHandler)) {
            this.resizeEventHandler();
        }
    }

    public onKeydown($event?: Event): void {
        if (!$event) return;
        const keyboardEvent = $event as KeyboardEvent;
        switch (keyboardEvent.keyCode) {
            case Keys.Down: //DOWN
                this.setNextActive();
                break;
            case Keys.Up: // UP
                this.setPrevActive();
                break;
            case Keys.Left: //LEFT
                this.setSubActive();
                break;
            case Keys.Right: //RIGHT
                this.setMainActive();
                break;
        }
    }

    public onKeyup($event?: Event): void {
        if (!$event) return;
        const keyboardEvent = $event as KeyboardEvent;
        if (keyboardEvent.keyCode === Keys.Esc) {
            this.close();
            if (isFunction(this.onEsc)) this.onEsc();
        }
        if (keyboardEvent.keyCode === Keys.Enter) {
            this.onItemSelect(this.getActiveItem());
        }
    }

    public onBlur($event?: Event): void {
        const focusedElement = document.activeElement;
        if (focusedElement && (this.element.element === focusedElement || // if newly focused element is inside menu component - IE11 requires this
            this.element.hasChild(focusedElement))) {
            this.element.focus();
            return;
        }
        this.close($event);
    }

    //#endregion

    //#region - active item

    private setFirstAvailableActive(collection = this.collection): void {
        const items = collection.filter(item => !(item?.isDisabled?.(this.context) ?? false));
        if (items[0]) {
            items[0].isActive = true;
        }
    }

    private getNextAvailableItem(collection: MenuItem[] | undefined, startIndex?: number): MenuItem | undefined {
        if (!collection) return undefined;
        const foundItem = (startIndex != null ? collection.filter((item, index) => index > startIndex) : [...collection])
            .find(item => !(item?.isDisabled?.(this.context) ?? false));
        if (foundItem) {
            return foundItem;
        } else {
            return this.getNextAvailableItem(collection);
        }
    }

    private setNextActive(): void {
        const activeItem = this.getActiveItem();
        if (activeItem) {
            const activeItemChunk = this.getItemChunk(activeItem);
            const activeItemIdx = activeItemChunk ? activeItemChunk.indexOf(activeItem) : -1;
            const nextItem = this.getNextAvailableItem(activeItemChunk, activeItemIdx);
            if (nextItem) this.setItemActive(nextItem);
        } else {
            this.setFirstAvailableActive();
        }
    }

    private getPrevAvailableItem(collection: MenuItem[] | undefined, startIndex?: number): MenuItem | undefined {
        if (!collection) return undefined;
        const foundItem = (startIndex != null ? collection.filter((item, index) => index < startIndex) : [...collection])
            .reverse()
            .find(item => !(item?.isDisabled?.(this.context) ?? false));
        if (foundItem) {
            return foundItem;
        } else {
            return this.getPrevAvailableItem(collection);
        }
    }

    private setPrevActive(): void {
        const activeItem = this.getActiveItem();
        if (activeItem) {
            const activeItemChunk = this.getItemChunk(activeItem);
            const activeItemIdx = activeItemChunk.indexOf(activeItem);
            const prevItem = this.getPrevAvailableItem(activeItemChunk, activeItemIdx);
            if (prevItem) this.setItemActive(prevItem);
        } else {
            this.setFirstAvailableActive();
        }
    }

    private setSubActive(): void {
        const activeItem = this.getActiveItem();
        if (!activeItem) return;
        if (activeItem.submenu != null) {
            this.setFirstAvailableActive(activeItem.submenu);
        }
    }

    private setMainActive(): void {
        const activeItem = this.getActiveItem();
        if (!activeItem) return;
        if (this.collection.indexOf(activeItem) === -1) { // is not inside main collection
            activeItem.isActive = false;
        }
    }

    private getActiveItem(): MenuItem {
        const flatItems = this.getFlatItems();
        const activeItems = flatItems.filter(item => item.isActive);
        return activeItems[activeItems.length - 1];
    }

    private getItemChunk(chosenItem: MenuItem): MenuItem[] {
        let itemChunk: MenuItem[] | undefined;
        const getItems = (items: MenuItem[] | undefined) => {
            if (!items) return;
            if (items.indexOf(chosenItem) !== -1) {
                itemChunk = items;
                return;
            } else {
                const itemsWithSubMenu = items.filter(item => item.submenu != null);
                if (itemsWithSubMenu.length) {
                    itemsWithSubMenu.forEach(item => {
                        getItems(item.submenu);
                    });
                }
            }
        };
        getItems(this.collection);
        return itemChunk ?? [];
    }

    public getFlatItems(): MenuItem[] {
        const flatItems: MenuItem[] = [];
        const getItems = (items: MenuItem[] | undefined) => {
            if (!items) return;
            items.forEach((item: MenuItem) => {
                flatItems.push(item);
                if (item.submenu) {
                    getItems(item.submenu);
                }
            });
        };
        getItems(this.collection);
        return flatItems;
    }

    public onPointerEnter(item: MenuItem, event?: PointerEvent | MouseEvent): void {
        if (event instanceof PointerEvent && event.pointerType === 'touch') {
            return;
        }
        this.setItemActive(item);
    }

    public setItemActive(item: MenuItem): void {
        if (item && !item.isActive && !(item?.isDisabled?.(this.context) ?? false)) {
            const itemChunk = this.getItemChunk(item);
            itemChunk
                .filter(itemInChunk => itemInChunk.isActive)
                .forEach(activeItem => activeItem.isActive = false);
            item.isActive = true;
        }
    }

    public onItemSelect(item: MenuItem): void {
        if (item?.isDisabled?.(this.context) ?? false) return;
        if (item.submenu) {
            this.setItemActive(item);
            return;
        }
        item?.click?.(this.context);
        if (item.closeAfterClick) {
            this.close();
        }
    }

    //#endregion
}
